import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentCourses } from '../../hooks/useLmsData';
import { Spinner } from '../ui/Spinner';
import {
  StudentActionLink,
  StudentEmpty,
  StudentPage,
  StudentSection,
} from './StudentUI';
import {
  formatStudentCurrency,
  getStudentCourseAction,
} from '../../utils/studentExperience';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'available', label: 'Open' },
  { key: 'active', label: 'My courses' },
  { key: 'payment_pending', label: 'Payment' },
  { key: 'request_submitted', label: 'Sent' },
];

const StudentCourseCatalog = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { data, loading, error } = useStudentCourses(currentUser?.uid);
  const [filter, setFilter] = useState('all');

  const courses = useMemo(() => {
    const courseMap = data?.courseMap || {};
    return (data?.allCourses || []).map((course) => ({
      ...course,
      ...(courseMap[course.id] || {}),
    }));
  }, [data]);

  const filteredCourses = useMemo(() => {
    if (filter === 'all') return courses;
    return courses.filter((course) => course.status === filter);
  }, [courses, filter]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <div className="py-8 text-center text-red-600">Failed to load courses.</div>;
  }

  return (
    <StudentPage
      title="Available courses"
      subtitle="Open a course to view details."
      action={
        <StudentActionLink to="/courses/my-courses" variant="secondary">
          My courses
        </StudentActionLink>
      }
    >
      <div className="flex flex-wrap gap-3">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
            className={`min-h-[44px] rounded-full px-4 text-sm font-semibold transition ${
              filter === item.key
                ? 'bg-[#d61f74] text-white shadow-[0_16px_28px_-18px_rgba(214,31,116,0.6)]'
                : 'bg-white text-stone-700 ring-1 ring-stone-200 hover:bg-stone-50'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <StudentSection title="Courses">
        {!filteredCourses.length ? (
          <StudentEmpty
            title="No courses found"
            message="Try another filter or check back later."
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onOpen={() => navigate(`/courses/${course.id}`)}
              />
            ))}
          </div>
        )}
      </StudentSection>
    </StudentPage>
  );
};

const CourseCard = ({ course, onOpen }) => {
  const action = getStudentCourseAction(course);
  const imageSrc = course.imageUrl || course.coverImage || '/default-course-image.jpg';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="overflow-hidden rounded-[24px] border border-stone-200 bg-white text-left shadow-sm transition hover:border-stone-300 hover:shadow-md"
    >
      <img
        src={imageSrc}
        alt={course.name}
        className="h-44 w-full object-cover"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = '/default-course-image.jpg';
        }}
      />
      <div className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="line-clamp-2 text-lg font-semibold text-stone-900">
              {course.name}
            </p>
            <p className="mt-1 text-sm text-stone-500">
              {course.type || 'Course'}
            </p>
          </div>
          <span className="shrink-0 text-xs font-medium text-stone-400">
            {course.batches?.[0]?.batchId || ''}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm text-stone-600">
          <Fact label="Fee" value={formatStudentCurrency(course.fees?.fullCourseFee)} />
          <Fact
            label="Duration"
            value={
              course.duration?.isOneDay
                ? 'One day'
                : `${course.duration?.months || 0} months`
            }
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-stone-100 pt-3">
          <span className="text-sm font-medium text-stone-700">View course</span>
          <span
            className="inline-flex min-h-[40px] items-center rounded-2xl bg-[#d61f74] px-4 text-sm font-semibold text-white shadow-[0_16px_30px_-18px_rgba(214,31,116,0.55)]"
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            {action.label}
          </span>
        </div>
      </div>
    </button>
  );
};

const Fact = ({ label, value }) => (
  <div className="rounded-2xl bg-stone-50 px-4 py-3">
    <p className="text-[11px] font-medium text-stone-500">
      {label}
    </p>
    <p className="mt-1.5 text-base font-semibold text-stone-950">{value}</p>
  </div>
);

export default StudentCourseCatalog;
